// src/components/common/cards/KPICard/KPICard.js
import React from 'react';
import { Card, Typography, Box, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';

const StyledCard = styled(Card)(({ theme, compact }) => ({
  height: compact ? 'auto' : '100%',
  minHeight: compact ? '80px' : '120px',
  maxHeight: compact ? '80px' : 'none',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: theme.spacing(compact ? 1.5 : 2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
}));

const CardContentContainer = styled(Box)(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(compact ? 1.5 : 2),
  height: '100%',
  padding: theme.spacing(compact ? 1.5 : 3),
}));

const IconContainer = styled(Box)(({ theme, iconcolor, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: compact ? 36 : 48,
  height: compact ? 36 : 48,
  borderRadius: '50%',
  backgroundColor: iconcolor || theme.palette.primary.main,
  color: 'white',
  flexShrink: 0,
}));

const TextContainer = styled(Box)(({ theme, compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  textAlign: 'center',
  gap: theme.spacing(compact ? 0.25 : 0.5),
}));

const RatingChip = styled(Chip)(({ theme, ratingcolor, ratingbg, compact }) => ({
  fontSize: compact ? '0.65rem' : '0.75rem',
  height: compact ? 20 : 24,
  borderRadius: compact ? 10 : 12,
  backgroundColor: ratingbg || theme.palette.grey[200],
  color: ratingcolor || theme.palette.text.secondary,
  '& .MuiChip-label': {
    fontWeight: 600,
  },
}));

/**
 * KPICard - Pure Display Component
 * 
 * A pure UI component that displays pre-calculated values with optional rating system.
 * All business logic and calculations should be done by the calling component.
 * 
 * @param {Object} props
 * @param {string} props.title - Card title/label
 * @param {string|number} props.value - Pre-calculated display value
 * @param {string} [props.type='number'] - Value formatting type: 'currency', 'number', 'percentage', 'rating'
 * @param {string} [props.iconType='trending'] - Icon type: 'money', 'trending', 'check', 'analytics', 'person', 'timer'
 * @param {string} [props.iconColor] - Custom icon background color (hex/rgb)
 * @param {Object} [props.rating] - Rating configuration object with ranges array
 * @param {string|number} [props.ratingValue] - Separate value for rating calculation (if different from display value)
 * @param {Object} [props.comparison] - Comparison configuration {actual, projected} for variance chip
 * @param {boolean} [props.compact=false] - Compact mode for reduced height and spacing
 * @param {boolean} [props.loading=false] - Loading state
 * @param {string} [props.error] - Error message to display
 * @param {Object} [props.sx] - Additional MUI sx props for styling
 */
const KPICard = ({
  title,
  value,
  type = 'number',
  iconType = 'trending',
  iconColor,
  rating = null,
  ratingValue = null, // Separate value for rating calculation
  comparison = null,
  compact = false,
  loading = false,
  error = null,
  sx = {},
  ...props
}) => {
  // Icon mapping
  const getIcon = (iconType) => {
    const iconMap = {
      money: <AttachMoneyIcon />,
      check: <CheckCircleIcon />,
      analytics: <AnalyticsIcon />,
      person: <PersonIcon />,
      timer: <TimerIcon />,
      trending: <TrendingUpIcon />
    };
    return iconMap[iconType] || iconMap.trending;
  };

  // Format value based on type
  const formatValue = (value, type) => {
    if (loading) return '...';
    if (error) return 'Error';
    if (value === null || value === undefined) return '--';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      case 'percentage':
        return `${value}%`;
      case 'rating':
        return value.toString();
      case 'number':
      default:
        if (typeof value === 'number') {
          return new Intl.NumberFormat('en-US').format(value);
        }
        return value.toString();
    }
  };

  // Parse value for rating calculation - supports numbers, percentages, and fractions
  const parseValueForRating = (value) => {
    if (typeof value === 'number') {
      return value;
    }
    
    if (typeof value === 'string') {
      const stringValue = value.trim();
      
      // Handle percentage strings (e.g., "75%")
      if (stringValue.endsWith('%')) {
        return parseFloat(stringValue.slice(0, -1));
      }
      
      // Handle fraction strings (e.g., "3/5", "15/20")
      if (stringValue.includes('/')) {
        const [numerator, denominator] = stringValue.split('/').map(s => parseFloat(s.trim()));
        if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
          return (numerator / denominator) * 100; // Convert to percentage
        }
        return null; // Invalid fraction
      }
      
      // Handle regular numeric strings
      const numericValue = parseFloat(stringValue);
      if (!isNaN(numericValue)) {
        return numericValue;
      }
    }
    
    return null;
  };

  // Get rating for current value
  const getRatingForValue = (value, ratingConfig) => {
    // Only hide rating if there's an error or no rating config, not during brief loading transitions
    if (!ratingConfig?.ranges || error) return null;
    
    const numericValue = parseValueForRating(value);
    if (numericValue === null) return null;
    
    const range = ratingConfig.ranges.find(r => 
      numericValue >= r.min && numericValue <= r.max
    );
    
    return range || null;
  };

  const currentRating = getRatingForValue(ratingValue || value, rating);

  // Dynamic sizing based on compact mode
  const titleVariant = compact ? 'caption' : 'subtitle2';
  const valueVariant = compact ? 'h6' : 'h4';

  // Error state
  if (error) {
    return (
      <StyledCard sx={sx} compact={compact} {...props}>
        <CardContentContainer compact={compact}>
          <IconContainer iconcolor="#f44336" compact={compact}>
            <CheckCircleIcon />
          </IconContainer>
          <TextContainer compact={compact}>
            <Typography variant={titleVariant} color="error" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant={compact ? 'caption' : 'body2'} color="error">
              {error}
            </Typography>
            {/* Invisible spacer for consistent 3-element layout */}
            <Box sx={{ height: compact ? 20 : 24, visibility: 'hidden' }} />
          </TextContainer>
        </CardContentContainer>
      </StyledCard>
    );
  }

  return (
    <StyledCard sx={sx} compact={compact} {...props}>
      <CardContentContainer compact={compact}>
        {/* Icon on the left */}
        <IconContainer iconcolor={iconColor} compact={compact}>
          {getIcon(iconType)}
        </IconContainer>

        {/* Text content centered on the right */}
        <TextContainer compact={compact}>
          {/* Line 1: Title */}
          <Typography 
            variant={titleVariant} 
            color="textSecondary" 
            sx={{ 
              fontWeight: 500,
              opacity: loading ? 0.5 : 1,
              fontSize: compact ? '0.75rem' : undefined,
            }}
          >
            {title}
          </Typography>

          {/* Line 2: Value */}
          <Typography 
            variant={valueVariant} 
            component="div" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              opacity: loading ? 0.5 : 1,
              lineHeight: compact ? 1.1 : 1.2,
            }}
          >
            {formatValue(value, type)}
          </Typography>

          {/* Line 3: Rating/Comparison chip or invisible spacer for centering */}
          {(() => {
            // Show comparison chip if provided (takes precedence over rating)
            if (comparison && !loading && comparison.actual !== undefined && comparison.projected !== undefined) {
              const variance = comparison.actual - comparison.projected;
              const isPositive = variance >= 0;
              const percentVariance = comparison.projected !== 0 
                ? Math.abs((variance / comparison.projected) * 100).toFixed(1)
                : 0;
              
              return (
                <RatingChip
                  label={`${isPositive ? '+' : '-'}${percentVariance}%`}
                  size="small"
                  ratingcolor={'#ffffff'}  // White text
                  ratingbg={isPositive ? '#4caf50' : '#f44336'}  // Green/red background
                  compact={compact}
                />
              );
            }
            // Show rating chip if provided and no comparison
            else if (currentRating && !loading) {
              return (
                <RatingChip
                  label={currentRating.label}
                  size="small"
                  ratingcolor={currentRating.color}
                  ratingbg={currentRating.bgColor}
                  compact={compact}
                />
              );
            }
            // Always render a spacer to maintain 3-element layout for proper centering
            return <Box sx={{ height: compact ? 20 : 24, visibility: 'hidden' }} />;
          })()}
        </TextContainer>
      </CardContentContainer>
    </StyledCard>
  );
};

export default KPICard;